import React from "react";
import { Box, Typography, Button } from "components/ui";
import Container from "components/general/Container";
import { Sparkle, Leaf, FlaskConical, Handshake, ShieldCheck, PackageSearch, UserCheck, Recycle, LifeBuoy, Award, Star } from "lucide-react-native";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { PageButton } from "./PageButton";

const benefitsContent = {
	enviroplus: [
		{
			title: "High-performing",
			description: "Enviroplus minimises environmental impact without compromising performance, using next-generation cleaning technologies that are powerful, effective, and designed to target the source of cleaning challenges.",
		},
		{
			title: "Biodegradable",
			description: "Enviroplus uses biodegradable ingredients that easily break down in water and soil, reducing the impacts of cleaning operations on the environment.",
		},
		{
			title: "Low toxicity",
			description: "Formulated with safer chemicals with low toxicity profiles to reduce negative health impacts and minimise harm to aquatic environments.",
		},
		{
			title: "Supply partners",
			description: "We collaborate with industry-leading chemists to develop high-performing, market-leading products, continually staying ahead of emerging innovations.",
		},
	],
	abcopro: [
		{
			title: "Professional Quality",
			description: "Abcopro delivers professional-grade products trusted by industry experts for reliability and performance.",
		},
		{
			title: "Comprehensive Range",
			description: "A wide selection of products to meet every cleaning and maintenance need, from chemicals to equipment.",
		},
		{
			title: "Expert Support",
			description: "Our team provides expert advice and support to help you get the best results from our products.",
		},
		{
			title: "Sustainability",
			description: "Abcopro is committed to sustainable practices and products that reduce environmental impact.",
		},
	],
	proquip: [
		{
			title: "Innovative Equipment",
			description: "Proquip offers the latest in cleaning equipment technology for maximum efficiency and results.",
		},
		{
			title: "Durability",
			description: "Built to last, Proquip products are designed for heavy-duty use in demanding environments.",
		},
		{
			title: "Service & Support",
			description: "Comprehensive service and support ensure your equipment stays in top condition.",
		},
		{
			title: "Trusted Brand",
			description: "Proquip is a trusted name in the industry, known for quality and reliability.",
		},
	],
};

export const PageBenefitsSection = ({ variant = "enviroplus" }: { variant?: "enviroplus" | "abcopro" | "proquip" }) => {
	const benefits = benefitsContent[variant] || [];
	const isMobile = useIsMobile();

	const iconMap: Record<string, any[]> = {
		enviroplus: [Sparkle, Leaf, FlaskConical, Handshake],
		abcopro: [ShieldCheck, PackageSearch, UserCheck, Recycle],
		proquip: [LifeBuoy, Award, Star, Star],
	};
	const icons = iconMap[variant] || [];

	return (
		<Box bg="$neutral25">
			<Container>
				<Box px={16} py={48}>
					<Box py={16}>
						<Typography weight="bold" sx={{ fontSize: 28, mb: 32 }}>{`Why ${variant.charAt(0).toUpperCase() + variant.slice(1)}?`}</Typography>
						<Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 16 : 24} mb={isMobile ? 24 : 16} alignItems={isMobile ? "center" : undefined}>
							{benefits.map((benefit, idx) => {
								const IconComponent = icons[idx] || Star;
								return (
									<Box key={idx} flex={1} minWidth={220} maxWidth={isMobile ? "100%" : 420} py={isMobile ? 48 : 24} backgroundColor="#fff" borderWidth={1} borderColor="#e0e0e0" borderRadius={8} p={24} mb={16}>
										<IconComponent color="#1a428a" size={40} style={{ minWidth: 40, marginBottom: 12 }} />
										<Typography weight="bold" sx={{ fontSize: 20, mb: 8 }}>
											{benefit.title}
										</Typography>
										<Typography sx={{ fontSize: 15 }}>{benefit.description}</Typography>
									</Box>
								);
							})}
						</Box>
						<Box display="flex" justifyContent="center" alignItems="center">
							<Box>
								<PageButton type="enquireForm" text="Enquire Now" variant="primary-light" size="large" />
							</Box>
						</Box>
					</Box>
				</Box>
			</Container>
		</Box>
	);
};
